import { useMemo, useState } from "react";
import "./index.css";
import Logo from "./Logo";
import heroBackground from "./bg.png";
import teamBackground from "./team.png";
import officeBackground from "./office.png";

type Page = "landing" | "reveal";

const CONFETTI_COLORS = ["#f8dfb5", "#f1c272", "#dba457", "#f9e7ce"] as const;

type ConfettiPiece = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  width: number;
  height: number;
  opacity: number;
  color: string;
};

function ConfettiBackground() {
  const confettiPieces = useMemo<ConfettiPiece[]>(() => {
    return Array.from({ length: 60 }, (_, index) => ({
      id: index,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 5 + Math.random() * 4,
      width: 8 + Math.random() * 8,
      height: 3 + Math.random() * 5,
      opacity: 0.45 + Math.random() * 0.4,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    }));
  }, []);

  return (
    <div className="confetti-overlay" aria-hidden="true">
      {confettiPieces.map((piece) => (
        <span
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            width: `${piece.width}px`,
            height: `${piece.height}px`,
            opacity: piece.opacity,
            background: piece.color,
            animationDelay: `${piece.delay}s, ${piece.delay / 2}s`,
            animationDuration: `${piece.duration}s, 2.4s`,
          }}
        />
      ))}
    </div>
  );
}

export function App() {
  const [page, setPage] = useState<Page>("landing");

  return (
    <main className="relative flex min-h-screen w-full items-center justify-center overflow-y-auto py-8">
      <div className="pointer-events-none absolute inset-0 h-screen">
        <img
          src={page === "landing" ? heroBackground : officeBackground}
          alt="Robotic car wash in San Francisco"
          className="h-screen w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-[#120b03]/80" />
      </div>
      {page === "reveal" && <ConfettiBackground />}
      {page === "landing" ? (
        <section className="relative z-10 isolate w-full max-w-5xl overflow-hidden rounded-[32px] border border-[#c7a461]/30 bg-black/80 px-6 py-10 text-left text-white shadow-[0_45px_120px_rgba(0,0,0,0.65)] sm:px-14 sm:py-14 mx-4">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-36 -right-32 h-80 w-80 rounded-full bg-[#b48d4e] opacity-40 blur-3xl" />
            <div className="absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-[#f4da9a] opacity-20 blur-[140px]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-transparent to-transparent" />
          </div>
          <div className="relative space-y-6">
            <p className="text-sm uppercase tracking-[0.4em] text-[#c7a461]">
              <Logo fill="#c7a461" className="size-4 inline -mt-1.5" />{" • "}
              Toma • Private Detail Studio
            </p>
            <h1 className="text-3xl font-semibold leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
              San Francisco's most advanced wash, run by an AI startup.
            </h1>
            <p className="text-lg leading-relaxed text-zinc-300">
              After running the numbers, the Toma is shutting down its AI customer support workforce and turning its attention to building the best robotic car wash in the world.
            </p>
          </div>

          <div className="relative mt-10 flex flex-wrap gap-6 text-sm font-semibold uppercase tracking-[0.4em] text-[#d8bc86]">
            <span className="rounded-full border border-[#c7a461]/60 px-6 py-3">
              288 Carolina St • Potrero Hill
            </span>
            <button
              onClick={() => setPage("reveal")}
              className="cursor-pointer rounded-full border border-transparent bg-[#c7a461] px-8 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-black shadow-[0_20px_40px_rgba(199,164,97,0.35)] transition-colors hover:bg-[#dbb56d]"
            >
              Schedule a Wash
            </button>
          </div>

          <div className="relative mt-14 grid gap-8 text-sm text-zinc-400 sm:grid-cols-3">
            <div>
              <p className="text-4xl font-semibold text-white">
                12<span className="ml-2 text-sm text-[#c7a461]">heated bays</span>
              </p>
              <p className="mt-2 leading-relaxed">
                Every stall is outfitted with lidar-guided sprayers tuned to automotive-grade
                standards normally reserved for autonomous fleets.
              </p>
            </div>
            <div>
              <p className="text-4xl font-semibold text-white">
                24<span className="ml-2 text-sm text-[#c7a461]">karat finish</span>
              </p>
              <p className="mt-2 leading-relaxed">
                Matte gold trims, champagne microfiber, and a playlist curated by the same
                algorithms that power our AI copilots.
              </p>
            </div>
            <div>
              <p className="text-4xl font-semibold text-white">
                1<span className="ml-2 text-sm text-[#c7a461]">bold pivot</span>
              </p>
              <p className="mt-2 leading-relaxed">
                Because if software margins are good, the economics of perfectly detailed steel
                are apparently immaculate.
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section className="relative z-10 isolate w-full max-w-4xl overflow-hidden rounded-[32px] border border-[#c7a461]/40 bg-black/85 px-6 py-10 text-left text-white shadow-[0_45px_120px_rgba(0,0,0,0.55)] sm:px-14 sm:py-14 mx-4">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 right-0 h-64 w-64 rounded-full bg-[#f4da9a] opacity-30 blur-[120px]" />
            <div className="absolute -bottom-32 left-8 h-72 w-72 rounded-full bg-[#b48d4e] opacity-30 blur-[160px]" />
          </div>

          <div className="relative space-y-4">
            <p className="text-sm uppercase tracking-[0.4em] text-[#c7a461]">April Fools!</p>
            <h2 className="text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
              We're still very much <a href="https://www.toma.com" target="_blank" rel="noreferrer" className="text-[#c7a461] hover:underline">Toma</a>, the automotive AI company.
            </h2>
            <img src={teamBackground} alt="Toma team" className="w-full max-w-2xl h-auto rounded-lg" />
            <p className="text-lg leading-relaxed text-zinc-300">
              Yes, we adore a spotless GT3, but Toma is thriving, shipping faster than ever, and helping the automotive world
              make better decisions with AI.
            </p>
            <div className="grid gap-2 leading-relaxed text-zinc-300 text-sm">
              <p>
                The joke: our "pivot" to a car wash is just today’s love letter to clean cars
                and a thriving, technologically underserved industry. The truth: we’re actively hiring, growing, and building the future of automotive AI.
              </p>
            </div>
          </div>

          <div className="relative mt-12 flex flex-wrap gap-4">
            <a
              href="https://www.toma.com/contact"
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-[#c7a461] px-8 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-black shadow-[0_20px_40px_rgba(199,164,97,0.35)] transition-colors hover:bg-[#dbb56d]"
            >
              Book a Demo
            </a>
            <a
              href="https://www.toma.com/jobs"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-[#c7a461]/70 px-8 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-[#e6cca0] transition-colors hover:border-[#dbb56d] hover:text-white"
            >
              Join the Team
            </a>
          </div>
        </section>
      )}
    </main>
  );
}

export default App;
